<script>
  import SectionTitle from "../../layout/SectionTitle.svelte"
  import { getNotificationsContext } from "svelte-notifications"
  import { useQueryClient } from "@sveltestack/svelte-query"
  import { createForm } from "felte"
  import { z } from "zod"
  import { validator } from "@felte/validator-zod"
  import PrimaryButton from "../../button/generic/PrimaryButton.svelte"
  import { translate } from "../../../i18n/i18next.js"
  import { useInvalidateLicenseData } from "../../../hooks/license/useLicenseData.js";
  import SectionCaption from "../../layout/SectionCaption.svelte";
  import ProjectSettingsTextInput from "./parts/ProjectSettingsTextInput.svelte";
  import ProjectSettingsTimezoneInput from "./parts/ProjectSettingsTimezoneInput.svelte";
  import ProjectSettingsPlanInput from "./parts/ProjectSettingsPlanInput.svelte";

  export let project

  const {addNotification} = getNotificationsContext()
  const queryClient = useQueryClient()
  const {invalidateLicenseData} = useInvalidateLicenseData()

  const schema = z.object({
    name: z.string().min(1, translate("frontend:pages:projects.project.admin.sections.generalInfo.form.projectName.errors.noProjectName")),
    description: z.union([
      z.string(),
      z.null(),
    ]),
    timezone: z.string(),
    plan: z.union([
      z.literal("standard"),
      z.literal("viewer"),
      z.literal("free"),
    ]),
  })

  async function handleSubmit(values) {
    const response = await fetch(`/api/projects/${project.id}`, {
      method: "PUT",
      body: JSON.stringify({
        name: values.name,
        description: values.description,
        timezone: values.timezone,
        plan: values.plan,
      }),
      headers: {
        "Content-Type": "application/json",
      }
    })
    if (response.ok) {
      addNotification({
        text: translate("frontend:generic.notifications.saved"),
        type: "success",
        removeAfter: 4000,
        position: "top-right",
      })
      await queryClient.invalidateQueries(`/api/projects/${project.id}`)
      await invalidateLicenseData()
    } else {
      addNotification({
        text: translate("frontend:generic.notifications.saveFailed"),
        type: "error",
        removeAfter: 4000,
        position: "top-right",
      })
    }
  }

  const {form, errors, data} = createForm({
    onSubmit: handleSubmit,
    initialValues: {...project},
    extend: validator({schema})
  })
</script>

<SectionTitle title={translate("frontend:pages:projects.project.admin.sections.generalInfo.title")} />
<SectionCaption>
  {translate("frontend:pages:projects.project.admin.sections.generalInfo.caption")}
</SectionCaption>
<form id="form-project-general-info" use:form>
  <ProjectSettingsTextInput
    label={translate("frontend:pages:projects.project.admin.sections.generalInfo.form.projectName.label")}
    name="name"
    error={$errors.name}
  />
  <ProjectSettingsTextInput
    label={translate("frontend:pages:projects.project.admin.sections.generalInfo.form.projectDescription.label")}
    name="description"
  />
  <ProjectSettingsTimezoneInput
    name="timezone"
    currentProjectTimezone={project.timezone}
    currentSelectedTimezone={$data.timezone}
  />
  <ProjectSettingsPlanInput
    name="plan"
    currentProjectPlan={project.plan}
  />
  <div>
    <PrimaryButton type="submit">{translate("generic.save")}</PrimaryButton>
  </div>
</form>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-top: 1rem;
  }
</style>